/**
 * The project's audit log (`MCK-20260521-009`, C7).
 *
 * One list, newest first, with three things per row: who, what, and when.
 *
 * **The scoped notice is not decoration.** When the server says the caller's zones bounded the
 * answer, the screen says so — `MCK-20260521-005` invariant 6: a filtered log that stays quiet
 * about being filtered reads as an absence of events, which is the one failure an audit trail
 * cannot have. So the notice renders above the list, before anything can be misread.
 *
 * **The filter bar and the download are one question.** The bar writes the URL, the URL is the
 * filter object, and the export button hands that same object to the CSV endpoint — so
 * *"export what is on screen"* holds because there is only one notion of what is on screen
 * (`./filters.ts`, `./ActivityFilterBar.tsx`).
 *
 * **An unknown event type prints its own name.** New event types land in this repo faster than
 * copy for them does, and a row that renders as blank is a row that looks like a bug. The raw
 * type is ugly on purpose: it reads as "we have not written this sentence yet" rather than as
 * "nothing happened". The vocabulary itself lives in `./events.ts`, because C1's Overview card
 * renders the same rows and used to render them raw.
 */

import { useState } from 'react';

import { Button, EmptyState, InlineError, Screen, ScreenHead, SkeletonRows } from '@/components';
import { membersQueryOptions } from '@/features/people';
import { relativeTime } from '@/features/projects';
import { useLocale, useMessages } from '@/lib/i18n';
import { useInfiniteSentinel } from '@/lib/infinite';
import { useQuery } from '@tanstack/react-query';

import styles from './Activity.module.css';
import { ActivityFilterBar } from './ActivityFilterBar';
import { eventSentence } from './events';
import type { ActivityFilters } from './filters';
import { isNarrowed } from './filters';
import { activityErrorKey } from './problems';
import { useActivity } from './queries';

export interface ActivityPageProps {
  readonly projectId: string;
  /** What the URL is asking for. The route parses it; this screen never holds a second copy. */
  readonly filters: ActivityFilters;
}

export function ActivityPage({ projectId, filters }: ActivityPageProps) {
  const t = useMessages('activity');
  const { locale } = useLocale();
  const log = useActivity(projectId, filters);
  // State, not a ref: a ref's `.current` changing does not re-render, so the effect that
  // observes the sentinel would never see it arrive.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  useInfiniteSentinel(sentinel, {
    hasNextPage: log.hasNextPage,
    isFetchingNextPage: log.isFetchingNextPage,
    fetchNextPage: () => {
      void log.fetchNextPage();
    },
  });
  const members = useQuery(membersQueryOptions(projectId));

  const nameOf = (userId: string | null | undefined): string => {
    if (userId == null) return t('actor.system');
    const member = members.data?.items.find((row) => row.user_id === userId);
    return member?.display_name ?? member?.email ?? t('actor.unknown');
  };

  const events = log.data?.pages.flatMap((page) => page.items) ?? [];
  const scoped = log.data?.pages[0]?.scoped ?? false;

  return (
    // The `Screen` frame (`KLQ-20260826-001`): one region scrolls, and it is the body.
    // The screen's own name and the chrome above it stay where they were put.
    <Screen
      head={
        // Level 2: the log is a screen inside a project, whose name the shell's tab row
        // already carries as the document's `h1` (`STYLE.md` §6).
        <ScreenHead level={2} title={t('page.title')} subtitle={t('page.subtitle')} />
      }
      // `Screen`'s one slot that never scrolls (`KLQ-20260910-001` §A). The bar used to sit
      // inside the body, so reaching an event from three weeks ago meant scrolling the
      // controls that would have found it straight off the screen.
      toolbar={
        <ActivityFilterBar projectId={projectId} filters={filters} ready={log.isSuccess} />
      }
    >
      <div className={styles.body}>
        {scoped ? (
          <p className={styles.scoped} role="note">
            {t('scoped.notice')}
          </p>
        ) : null}

        {log.isError ? (
          <InlineError variant="block">{t(activityErrorKey(log.error))}</InlineError>
        ) : log.isPending ? (
          // An event row is a sentence and the moment it happened, stacked; the placeholder is
          // one bar for each at the height the pair occupies, so the log does not jump when
          // the first page lands.
          <SkeletonRows
            label={t('status.loading')}
            rows={5}
            rowHeight={48}
            columns={['62%', 110]}
          />
        ) : events.length === 0 ? (
          // Two different sentences, because they are two different facts. A filtered log that
          // says *nothing has been recorded yet* is the one wrong answer an audit trail cannot
          // give, and it is the answer a single empty state would give every time somebody
          // picked a filter that matches nothing.
          <EmptyState
            variant="list"
            // TODO(-014): placeholder sentence in the brief's voice; wave 3 writes the real one.
            explains={t('status.explains')}
            title={isNarrowed(filters) ? t('status.noMatchTitle') : t('status.emptyTitle')}
            body={isNarrowed(filters) ? t('status.noMatch') : t('status.empty')}
          />
        ) : (
          <ol className={styles.list}>
            {events.map((event) => (
              <li key={event.id} className={styles.row}>
                <span className={styles.what}>
                  <span className={styles.who}>{nameOf(event.actor_user_id)}</span>{' '}
                  {eventSentence(t, event)}
                </span>
                <span className={styles.meta}>
                  {event.actor_role_at_event_time != null
                    ? `${t('role.at', { role: event.actor_role_at_event_time })} · `
                    : ''}
                  {relativeTime(event.occurred_at, locale)}
                </span>
              </li>
            ))}
          </ol>
        )}

        {/* The next page arrives on the scroll; the button stays as the fallback, because a
            list that can only be advanced by scrolling cannot be finished on a keyboard
            (`lib/infinite.ts`). */}
        {log.hasNextPage ? (
          <div ref={setSentinel} className={styles.sentinel}>
            <Button
              size="sm"
              disabled={log.isFetchingNextPage}
              onClick={() => {
                void log.fetchNextPage();
              }}
            >
              {log.isFetchingNextPage ? t('list.loadingMore') : t('list.more')}
            </Button>
          </div>
        ) : null}
      </div>
    </Screen>
  );
}
